/**
 * StandardEmptyState Component
 *
 * Unified empty state component with action button.
 * Used across all applications for consistent empty UX.
 *
 * @example
 * <StandardEmptyState
 *   icon={<Inbox size={48} />}
 *   title="No records found"
 *   message="Get started by creating your first record."
 *   actionLabel="Create Record"
 *   onAction={handleCreate}
 * />
 */

import React from 'react';
import { Inbox } from 'lucide-react';
import { StandardButton } from './StandardButton';

export interface StandardEmptyStateProps {
  icon?: React.ReactNode;
  title: string;
  message?: string;
  actionLabel?: string;
  onAction?: () => void;
  secondaryActionLabel?: string;
  onSecondaryAction?: () => void;
}

export const StandardEmptyState: React.FC<StandardEmptyStateProps> = ({
  icon,
  title,
  message,
  actionLabel,
  onAction,
  secondaryActionLabel,
  onSecondaryAction
}) => {
  return (
    <div className="flex items-center justify-center py-12 px-4">
      <div className="text-center max-w-md mx-auto">
        {/* Icon */}
        <div className="flex justify-center mb-4">
          <div className="rounded-full bg-gray-100 dark:bg-gray-800 p-4">
            {icon ? (
              <div className="text-gray-400 dark:text-gray-600">
                {icon}
              </div>
            ) : (
              <Inbox className="text-gray-400 dark:text-gray-600" size={48} />
            )}
          </div>
        </div>

        {/* Title */}
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
          {title}
        </h3>

        {/* Message */}
        {message && (
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            {message}
          </p>
        )}

        {/* Actions */}
        {(onAction || onSecondaryAction) && (
          <div className="flex items-center justify-center gap-3">
            {onAction && actionLabel && (
              <StandardButton variant="primary" onClick={onAction}>
                {actionLabel}
              </StandardButton>
            )}
            {onSecondaryAction && secondaryActionLabel && (
              <StandardButton variant="secondary" onClick={onSecondaryAction}>
                {secondaryActionLabel}
              </StandardButton>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
